import { useLocalStorageState } from "@sundae/react-hooks";
import noop from "lodash/noop";
import { createContext, useContext, useState } from "react";
import FirstVisitDialogs from "../../components/FirstVisitDialogs/FirstVisitDialogs";
import HotjarFirstVisitDialog from "../../components/FirstVisitDialogs/HotjarFirstVisitDialog";
import { LOCAL_STORAGE_FIRST_VISIT_KEY } from "../../constants/localStorage.constants";
import { IFirstVisitContextProps, IFirstVisitContextState } from "./types";
import WelcomeDialog from "../../components/FirstVisitDialogs/WelcomeDialog";
import ErrorReportingDialog from "../../components/FirstVisitDialogs/ErrorReportingDialog";
import { featureIsEnabled } from "../../utils/features.utils";

const FirstVisitContext = createContext<IFirstVisitContextState>({
  components: [],
  currentComponentIndex: 0,
  handleOnNext: noop,
  handleOnSpecificIndex: noop,
});

export const FirstVisitContextProvider = ({ children }: IFirstVisitContextProps) => {
  const [isFirstVisit, setIsFirstVisit] = useLocalStorageState(LOCAL_STORAGE_FIRST_VISIT_KEY, true);
  const [currentComponentIndex, setCurrentComponentIndex] = useState(0);

  // The order of the components is important here as it determines the order of the dialogs that will be shown to the user.
  const components = [
    WelcomeDialog,
    ErrorReportingDialog,
    ...(featureIsEnabled("hotjar") ? [HotjarFirstVisitDialog] : []),
  ];

  // This is used to handle the case when the user clicks on one of the dots to render a specific dialog.
  const handleOnSpecificIndex = (index: number) => {
    setCurrentComponentIndex(index);
    if (index === components.length) {
      setIsFirstVisit(false);
    }
  };

  // This is used to handle the case when the user clicks on the next button to render the next dialog.
  const handleOnNext = () => {
    if (isFirstVisit && currentComponentIndex !== components.length - 1) {
      return setCurrentComponentIndex((prev) => prev + 1);
    }

    return setIsFirstVisit(false);
  };

  return (
    <FirstVisitContext.Provider
      value={{
        components,
        currentComponentIndex,
        handleOnNext,
        handleOnSpecificIndex,
      }}
    >
      {isFirstVisit && <FirstVisitDialogs />}
      {children}
    </FirstVisitContext.Provider>
  );
};

export const useFirstVisitContext = (): IFirstVisitContextState => useContext(FirstVisitContext);
